import React from "react";

import { faClockRotateLeft } from "@fortawesome/pro-regular-svg-icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useSearchParams } from "react-router-dom";

import { useModules } from "src/api/modulesApi";
import { useUserOrganizations, useWorkspaces } from "src/api/queries";
import { oauthApi } from "src/api/taktileApi";
import { Card } from "src/base-components/Card";
import { FormItem } from "src/base-components/FormItem";
import { Option, Section, Select } from "src/base-components/Select";
import {
  ModuleKind,
  WorkspaceDataplane,
  WorkspaceStatus,
} from "src/clients/flow-api";
import {
  OAuthAuthorizationRequestInfo,
  OAuthClientRegistration,
  OAuthConsentRequest,
  OrganizationInDB,
} from "src/clients/taktile-api";
import { Button } from "src/design-system/Button";
import { Callout } from "src/design-system/Callout";
import { Icon } from "src/design-system/Icon";
import { Pill, PillVariants } from "src/design-system/Pill";
import { Skeleton } from "src/design-system/Skeleton";
import { isMcpModuleServing } from "src/hooks/useIsMcpServerEnabled";
import {
  RecentWorkspace,
  useRecentWorkspaces,
} from "src/utils/useRecentWorkspaces";

type ConsentError = {
  error: string;
  error_description: string;
};

type WorkspaceGate = (ws: WorkspaceDataplane) => boolean;

// The registration path decides the flow, and every id shape is ours: taktile-api
// mints the `dcr-` prefix, CIMD requires an https metadata URL, and pre-registered
// ids are fixed strings. An unrecognised id falls through to the cp_proxy gate.
const DCR_CLIENT_ID_PREFIX = "dcr-";
const CIMD_CLIENT_ID_PREFIX = "https://";
const MCP_CLIENT_ID = "taktile-mcp";

const isMcpClient = (clientId: string | null): boolean =>
  clientId !== null &&
  (clientId.startsWith(DCR_CLIENT_ID_PREFIX) ||
    clientId.startsWith(CIMD_CLIENT_ID_PREFIX) ||
    clientId === MCP_CLIENT_ID);

const MCP_MODULE_PAGE_LIMIT = 1000;

// /authorize applies no length cap to a CIMD client_id, and client_name is
// caller-chosen for every self-registered client.
const CLIENT_NAME_MAX_LENGTH = 48;
const CLIENT_URI_MAX_LENGTH = 64;

const bounded = (value: string, max: number): string =>
  value.length > max ? `${value.slice(0, max)}\u2026` : value;

const TRUST: Record<
  OAuthClientRegistration,
  { variant: PillVariants; label: string }
> = {
  [OAuthClientRegistration.PRE_REGISTERED]: {
    variant: "green",
    label: "Verified",
  },
  [OAuthClientRegistration.CIMD]: {
    variant: "green",
    label: "Verified domain",
  },
  [OAuthClientRegistration.DCR]: { variant: "yellow", label: "Unverified" },
};

const clientCautions = (client: OAuthAuthorizationRequestInfo): string[] =>
  [
    client.registration === OAuthClientRegistration.DCR
      ? "Anyone can register a client under any name, so Taktile cannot confirm who this is."
      : null,
    client.registration !== OAuthClientRegistration.PRE_REGISTERED &&
    client.all_redirect_uris_loopback
      ? "Access goes to a program on this computer, so continue only if you started this yourself."
      : null,
  ].filter((caution): caution is string => caution !== null);

const urlHost = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try {
    return new URL(url).host.toLowerCase();
  } catch {
    return null;
  }
};

const WORKSPACE_STATUS_TOOLTIPS: Record<
  WorkspaceStatus,
  { variant: PillVariants; label: string } | null
> = {
  [WorkspaceStatus.SUCCESS]: null, // Don't display tooltip for succeeded workspaces
  [WorkspaceStatus.PENDING]: {
    variant: "indigo-light",
    label: "Update Pending",
  },
  [WorkspaceStatus.FAILURE]: { variant: "yellow", label: "Update Failed" },
};

const WorkspaceOptionLabel: React.FC<{ ws: WorkspaceDataplane }> = ({ ws }) => {
  const tooltip = WORKSPACE_STATUS_TOOLTIPS[ws.status];
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate">{ws.name}</span>
      {tooltip && (
        <Pill size="sm" variant={tooltip.variant}>
          <Pill.Text>{tooltip.label}</Pill.Text>
        </Pill>
      )}
    </span>
  );
};

const SelectedWorkspace: React.FC<{ orgName?: string; wsName: string }> = ({
  orgName,
  wsName,
}) => (
  <FormItem gap={false} label="Workspace">
    <div
      className="font-inter-normal-13px text-gray-800"
      data-loc="selected-workspace"
    >
      {orgName ? `${orgName} | ${wsName}` : wsName}
    </div>
  </FormItem>
);

// CIMD resolves the client_id as a URL, so its host is the one thing about the
// client that is checked. A DCR client_id is an opaque token and names nothing.
const clientOrigin = (client: OAuthAuthorizationRequestInfo): string | null =>
  client.registration === OAuthClientRegistration.CIMD
    ? urlHost(client.client_id)
    : null;

// The frontend can reach a control plane that predates these fields, so the block
// appears only once one describes the client rather than asserting a default.
const describesClient = (client: OAuthAuthorizationRequestInfo): boolean =>
  Boolean(client.registration && TRUST[client.registration]);

const ClientIdentity: React.FC<{
  client: OAuthAuthorizationRequestInfo;
}> = ({ client }) => {
  const trust = TRUST[client.registration];
  const origin = clientOrigin(client);
  const redirect = client.redirect_uri_hostname ?? client.redirect_uri;
  return (
    <>
      <FormItem gap={false}>
        <div className="flex min-w-0 items-center gap-2">
          <Pill size="sm" variant={trust.variant}>
            <Pill.Text>{trust.label}</Pill.Text>
          </Pill>
          <span
            className="truncate font-inter-semibold-14px text-gray-800"
            data-loc="client-name"
          >
            {bounded(
              client.client_name ?? client.client_id,
              CLIENT_NAME_MAX_LENGTH,
            )}
          </span>
        </div>
        {origin !== null && (
          <div
            className="truncate font-inter-normal-12px text-gray-500"
            data-loc="client-origin"
          >
            {bounded(origin, CLIENT_URI_MAX_LENGTH)}
          </div>
        )}
      </FormItem>
      {redirect && (
        <FormItem gap={false} label="Sends the code to">
          <div
            className="font-inter-normal-13px text-gray-800"
            data-loc="redirect-host"
          >
            {bounded(redirect, CLIENT_URI_MAX_LENGTH)}
          </div>
        </FormItem>
      )}
    </>
  );
};

const getEligibleWorkspaces = (
  workspaces: WorkspaceDataplane[] | undefined,
  organizations: OrganizationInDB[] | undefined,
  isGranted: WorkspaceGate,
): WorkspaceDataplane[] => {
  if (!workspaces || !organizations) return [];

  const orgById = Object.fromEntries(
    organizations.map((org) => [org.id, org] as const),
  );

  return workspaces
    .filter(
      (ws) =>
        // base_url is optional and absent for not-yet-provisioned workspaces
        Boolean(ws.base_url) && orgById[ws.organization_id] && isGranted(ws),
    )
    .sort((a, b) => {
      const orgCompare = (orgById[a.organization_id]?.name ?? "").localeCompare(
        orgById[b.organization_id]?.name ?? "",
      );
      if (orgCompare !== 0) return orgCompare;
      return a.name.localeCompare(b.name);
    });
};

export const OAuthConsentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("request_id");
  const clientId = searchParams.get("client_id");

  const isMcpFlow = isMcpClient(clientId);

  const {
    data: authorizationRequest,
    isLoading: authorizationRequestLoading,
    isError: authorizationRequestFailed,
  } = useQuery({
    queryKey: ["oauthAuthorizationRequest", requestId],
    queryFn: async () =>
      (
        await oauthApi.getAuthorizationRequestApiV1OauthAuthorizationRequestsRequestIdGet(
          requestId!,
        )
      ).data,
    enabled: Boolean(requestId),
    staleTime: Infinity,
  });

  const { data: organizations, isLoading: orgsLoading } =
    useUserOrganizations();
  const { data: workspaces, isLoading: wsLoading } = useWorkspaces();
  const { data: mcpModules, isLoading: mcpModulesLoading } = useModules({
    kind: ModuleKind.MCP,
    pageLimit: MCP_MODULE_PAGE_LIMIT,
    enabled: isMcpFlow,
  });
  const { recentWorkspaces, addRecentWorkspace } = useRecentWorkspaces();

  const mcpWorkspaceIds = new Set(
    (mcpModules ?? [])
      .filter(isMcpModuleServing)
      .map((module) => module.workspaceId),
  );

  const isGranted: WorkspaceGate = isMcpFlow
    ? (ws) => mcpWorkspaceIds.has(ws.id)
    : (ws) => Boolean(ws.feature_gates?.cp_proxy);

  const eligibleWorkspaces = getEligibleWorkspaces(
    workspaces,
    organizations,
    isGranted,
  );

  const orgById = Object.fromEntries(
    (organizations ?? []).map((org) => [org.id, org] as const),
  );

  const sections: Section[] = [
    ...new Set(eligibleWorkspaces.map((ws) => ws.organization_id)),
  ].map((orgId) => ({
    key: orgId,
    displayValue: orgById[orgId]?.name ?? orgId,
  }));

  const options: Option[] = eligibleWorkspaces.map((ws) => ({
    key: ws.id,
    value: <WorkspaceOptionLabel ws={ws} />,
    displayValue: ws.name,
    searchText: `${ws.name} ${orgById[ws.organization_id]?.name ?? ""} ${
      WORKSPACE_STATUS_TOOLTIPS[ws.status]?.label ?? ""
    }`,
    sectionKey: ws.organization_id,
  }));

  const activeWsIds = new Set(eligibleWorkspaces.map((ws) => ws.id));
  const validRecentWorkspaces = recentWorkspaces.filter((rw) =>
    activeWsIds.has(rw.workspaceId),
  );

  const [pickedWsId, setPickedWsId] = React.useState<string | null>(null);

  // Only the MCP flow derives its workspace from the resource: /authorize binds one for
  // any client, and tktl-cli's picker has to stay as it is.
  const boundResource = isMcpFlow
    ? (authorizationRequest?.resource ?? null)
    : null;
  // An MCP client connects to one workspace's server, so the RFC 8707 resource it
  // sent to /authorize decides the workspace. A token scoped to any other one is
  // rejected by the server it was minted for.
  const boundHost = urlHost(boundResource);
  const resourceWs = boundHost
    ? (eligibleWorkspaces.find(
        (ws) => ws.base_url?.toLowerCase() === boundHost,
      ) ?? null)
    : null;

  // A resource that names no workspace of the user's is a server reached at an address
  // the control plane does not know, which is what a locally run MCP module looks like.
  // Fall back to choosing, rather than blocking a flow that can still complete, and say
  // so either way: silently reverting to a free choice is the footgun this screen closes.
  const resourceNotice =
    isMcpFlow && authorizationRequestFailed
      ? "Could not read which workspace this client connected to."
      : boundHost && !resourceWs && eligibleWorkspaces.length > 0
        ? `${boundHost} does not match one of your workspaces with the Taktile MCP module enabled.`
        : null;

  const showPicker =
    !resourceWs && (!isMcpFlow || eligibleWorkspaces.length > 1);
  const chosenWsId = resourceWs
    ? resourceWs.id
    : (pickedWsId ?? (showPicker ? null : (eligibleWorkspaces[0]?.id ?? null)));

  const chosenWs =
    eligibleWorkspaces.find((ws) => ws.id === chosenWsId) ?? null;
  const chosenOrg = chosenWs
    ? (orgById[chosenWs.organization_id] ?? null)
    : null;

  const mutation = useMutation({
    mutationFn: (body: OAuthConsentRequest) =>
      oauthApi.consentApiV1OauthConsentPost(body),
    onSuccess: (response) => {
      window.location.href = response.data.redirect_url;
    },
  });

  const errorMessage =
    mutation.error instanceof AxiosError
      ? ((mutation.error.response?.data as ConsentError | undefined)
          ?.error_description ?? "An unexpected error occurred")
      : mutation.isError
        ? "An unexpected error occurred"
        : undefined;

  if (!requestId || !clientId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <Callout variant="error">
          Invalid authorization request. Missing required parameters.
        </Callout>
      </div>
    );
  }

  // The name is in the identity row, so the title only carries it when that row
  // cannot appear.
  const cardTitle =
    authorizationRequest && !describesClient(authorizationRequest)
      ? `Authorize ${bounded(authorizationRequest.client_id, CLIENT_NAME_MAX_LENGTH)}`
      : "Authorize access";
  const cautions =
    authorizationRequest && describesClient(authorizationRequest)
      ? clientCautions(authorizationRequest)
      : [];

  const isLoading =
    orgsLoading ||
    wsLoading ||
    authorizationRequestLoading ||
    (isMcpFlow && mcpModulesLoading);
  const canAuthorize = Boolean(chosenWs);

  const handleSelectWorkspace = (wsId: string) => {
    setPickedWsId(wsId);
    trackRecent(wsId);
  };

  const trackRecent = (wsId: string) => {
    const ws = eligibleWorkspaces.find((w) => w.id === wsId);
    const org = ws ? orgById[ws.organization_id] : undefined;
    if (ws && org) {
      addRecentWorkspace({
        workspaceId: ws.id,
        workspaceName: ws.name,
        orgId: org.id,
        orgName: org.name,
      });
    }
  };

  const handleRecentClick = (recent: RecentWorkspace) => {
    setPickedWsId(recent.workspaceId);
    addRecentWorkspace(recent);
  };

  const handleAuthorize = () => {
    const body: OAuthConsentRequest = {
      request_id: requestId,
      ...(chosenOrg &&
        chosenWs && {
          org_id: chosenOrg.id,
          ws_id: chosenWs.id,
          workspace_slug: chosenWs.slug,
          workspace_name: chosenWs.name,
          workspace_base_url: chosenWs.base_url,
          principal: chosenOrg.principal,
          org_name: chosenOrg.name,
        }),
    };
    mutation.mutate(body);
  };

  const handleDeny = () => {
    mutation.mutate({ request_id: requestId, denied: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-[400px]">
        <Card variant="inverted">
          <Card.Header className="mb-1 p-2 pb-0">
            <Card.Title title={cardTitle} />
          </Card.Header>
          <div className="space-y-4 p-2">
            <p className="font-inter-normal-13px text-gray-500">
              {isMcpFlow
                ? "Authorize MCP access to a Taktile workspace."
                : "Select a workspace to authorize CLI access."}
            </p>

            {!isLoading && cautions.length > 0 && (
              <Callout dataLoc="client-caution" variant="warning">
                {cautions.join(" ")}
              </Callout>
            )}

            {!isLoading &&
              authorizationRequest &&
              describesClient(authorizationRequest) && (
                <ClientIdentity client={authorizationRequest} />
              )}

            {!isLoading && resourceNotice && (
              <Callout variant="warning">{resourceNotice}</Callout>
            )}

            {isLoading ? (
              <div className="space-y-3">
                <Skeleton height="h-8" />
                <Skeleton height="h-8" />
              </div>
            ) : showPicker ? (
              <>
                {validRecentWorkspaces.length > 0 && (
                  <FormItem gap={false}>
                    <FormItem.Label>
                      <span className="mr-1 inline-flex">
                        <Icon icon={faClockRotateLeft} size="xs" />
                      </span>
                      Recent
                    </FormItem.Label>
                    <div className="flex flex-wrap gap-2">
                      {validRecentWorkspaces.map((recent) => (
                        <Button
                          key={recent.workspaceId}
                          dataLoc={`recent-workspace-${recent.workspaceId}`}
                          variant={
                            chosenWsId === recent.workspaceId
                              ? "primary"
                              : "secondary"
                          }
                          size="sm"
                          onClick={() => handleRecentClick(recent)}
                        >
                          {recent.orgName} | {recent.workspaceName}
                        </Button>
                      ))}
                    </div>
                  </FormItem>
                )}

                <FormItem gap={false} label="Workspace">
                  <Select
                    searchable
                    dataLoc="workspace-select"
                    options={options}
                    placeholder="Search and select a workspace..."
                    searchPlaceholder="Search workspaces..."
                    sectionDividers
                    sections={sections}
                    value={chosenWsId}
                    onChange={handleSelectWorkspace}
                  />
                </FormItem>
              </>
            ) : chosenWs ? (
              <SelectedWorkspace
                orgName={chosenOrg?.name}
                wsName={chosenWs.name}
              />
            ) : (
              <Callout variant="warning">
                None of your workspaces has the Taktile MCP module enabled.
              </Callout>
            )}

            {errorMessage !== undefined && (
              <Callout variant="error">{errorMessage}</Callout>
            )}

            <div className="flex gap-3">
              <Button
                htmlType="button"
                variant="primary"
                isLoading={mutation.isPending && !mutation.variables?.denied}
                isDisabled={mutation.isPending || !canAuthorize}
                onClick={handleAuthorize}
                fullWidth
              >
                Authorize
              </Button>
              <Button
                htmlType="button"
                variant="secondary"
                isLoading={
                  mutation.isPending && mutation.variables?.denied === true
                }
                isDisabled={mutation.isPending}
                onClick={handleDeny}
                fullWidth
              >
                Deny
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
